import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Film } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Welcome back");
      navigate(redirectTo);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#090A0F] px-6 grain relative">
      <div className="w-full max-w-md glass rounded-2xl p-8 gold-glow fade-up" data-testid="login-card">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <Film className="w-6 h-6 text-[#D4AF37]" />
          <span className="font-display text-2xl text-white tracking-wide">PROPMOTION</span>
        </Link>
        <h1 className="font-display text-3xl text-white text-center mb-1">Welcome back</h1>
        <p className="text-sm text-slate-400 text-center mb-6">Sign in to your production dashboard</p>

        {error && <p className="text-sm text-red-400 mb-4 text-center" data-testid="login-error">{error}</p>}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-slate-300">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="login-email" />
          </div>
          <div>
            <Label className="text-slate-300">Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="mt-1.5 bg-[#12141D] border-slate-700 text-white" data-testid="login-password" />
          </div>
          <Button type="submit" disabled={busy}
            className="w-full bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full" data-testid="login-submit">
            {busy ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-slate-800" /><span className="text-xs text-slate-500">OR</span><div className="h-px flex-1 bg-slate-800" />
        </div>
        <Button variant="outline" onClick={googleLogin}
          className="w-full border-slate-700 text-slate-200 hover:bg-white/5 rounded-full" data-testid="google-login">
          Continue with Google
        </Button>

        <p className="text-sm text-slate-400 text-center mt-6">
          No account? <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="to-register">Create one</Link>
        </p>
      </div>
    </div>
  );
}
